<template>
  <div class="fashion-fix">
    <div v-if="typeof data === 'object'">
      <div v-if="data.header_image" class="image-section">
        <img
          :src="data.header_image.mobile_image"
          class="banner-image mobile-image"
          alt="Paparazzi Accessories Fashion Fix header image"
        />
        <img
          :src="data.header_image.desktop_image"
          class="banner-image desktop-image"
          alt="Paparazzi Accessories Fashion Fix header image"
        />
      </div>

      <div class="small-text">
        <h1 class="header">WELCOME TO THE FASHION FIX!</h1>
        <p>
          Paparazzi has revolutionized the way people accessorize, proving that
          you don’t need to break the bank to look like a million bucks. And
          with hot, new, trendy designs arriving regularly, it’s easier than
          ever to expand your horizons and discover new looks and styles that
          empower you.
        </p>
        <p>
          The Paparazzi Fashion Fix makes stylizing effortless: taking the
          guesswork out of accessorizing with its perfectly coordinated Trend
          Blends, and essentially putting the Paparazzi Stylists in your back
          pocket.
        </p>
        <p>
          The Fashion Fix is a monthly subscription, exclusively available to
          Paparazzi Consultants. Each delivery contains 25 new accessories,
          including five Trend Blends (four pieces each) from each of the
          following Paparazzi Collections: Fiercely 5th Avenue, Glimpses of
          Malibu, Magnificent Musings, Simply Santa Fe, and Sunset Sightings.
          Every month, you’ll also receive five exclusive Stylist Picks that you
          can’t get anywhere other than the Fashion Fix!
        </p>
        <p class="last">
          In addition to the 25 accessories, each monthly Fashion Fix delivery
          includes access to accompanying Style Guides for each Trend Blend.
          Packed with stylist insights and selling tips, these guides
          essentially put the Paparazzi Stylists in your back pocket, and make
          you look like an expert!
        </p>
      </div>

      <div class="categories">
        <RouterLink
          v-for="category in data.collections"
          :key="category.month.id"
          :to="{
            name: '/fashion-fix/[id]',
            params: { id: category.collection_id },
            query: { dbid: category.month.id },
          }"
          class="category"
        >
          <img
            v-if="category.month.collection_flat_lay_image"
            :src="category.month.collection_flat_lay_image"
            :alt="category.name"
          />
          <p>{{ category.name }}</p>
        </RouterLink>
      </div>
    </div>

    <div v-else-if="data === 'loading'">Loading...</div>

    <div v-else class="error-message">
      {{ data }}
      <button @click="loadData">Try Again</button>
    </div>

    <div class="archive" v-if="typeof months === 'object'">
      <p class="archive-header">ARCHIVE</p>

      <button
        v-for="date in months"
        @click="selectArchive(date.start_date, date.end_date)"
      >
        {{ archiveMonthTitle(date.end_date) }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  CollectionMonth,
  LandingImage,
} from "@/stores/defs/fashion_fix_defs";
import { archiveMonthTitle } from "@/utils/fashion_fix_utils";
import api from "@virgodev/bazaar/functions/api";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

const route = useRoute();
const router = useRouter();

const archiveMonths = ref<"loading" | "error" | CollectionMonth[]>("loading");
const data = ref<
  | "loading"
  | string
  | {
      collections: {
        month: CollectionMonth;
        name: string;
        collection_id: number;
      }[];
      header_image: LandingImage;
    }
>("loading");

const months = computed(() => {
  if (typeof archiveMonths.value === "object") {
    let array: CollectionMonth[] = [];
    archiveMonths.value.forEach((month) => {
      const filtered = array.find((compMonth) => {
        const date = new Date(month.end_date).getMonth();
        const compDate = new Date(compMonth.end_date).getMonth();

        return date === compDate;
      });

      if (filtered === undefined || array.length === 0) {
        array.push(month);
      }
    });

    if (typeof route.query.date === "string") {
      return array.filter((month) => {
        if (route.query.date) {
          return (
            route.query.date >= month.end_date ||
            route.query.date <= month.start_date
          );
        }
      });
    } else {
      const monthsWithoutFirst = array.slice(1);
      return monthsWithoutFirst;
    }
  }

  return "loading";
});

onMounted(() => {
  loadData();
});

function loadData() {
  data.value = "loading";
  getCollections();
  getArchiveMonths();
}

async function getCollections() {
  let url;
  if (route.query.date) {
    url = `collection_months/landing_archive/?date=${route.query.date}`;
  } else {
    url = "collection_months/landing_archive/";
  }

  const response = await api({ url: url });

  if (response.ok) {
    data.value = response.body;
  } else {
    data.value =
      "An unexpected error occured. Please check your connection and try again.";
  }
}

async function getArchiveMonths() {
  const response = await api({ url: "collection_months/archive_months/" });

  if (response.ok) {
    archiveMonths.value = response.body;
  }
}

function selectArchive(startDate: string, endDate: string) {
  const today = new Date();
  if (today >= new Date(startDate) && today <= new Date(endDate)) {
    router.push("/fashion-fix/");
  } else {
    const dateSelected = new Date(endDate);
    const startOfMonth = new Date(
      dateSelected.getFullYear(),
      dateSelected.getMonth(),
      1,
    );
    const dateInISO = startOfMonth.toISOString().split("T")[0];

    router.push(`/fashion-fix/?date=${dateInISO}`);
  }
}

watch(route, () => {
  loadData();
});
</script>

<style scoped>
/* Global */

.page {
  margin-bottom: 0;
}
.banner-image {
  object-fit: cover;
  height: 100%;
  width: 100%;
}
.header {
  font-size: 18px;
  font-weight: 300;
  text-transform: uppercase;
}
.small-text {
  p {
    font-family: Indivisible;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    margin-top: 0;
  }
  p.last {
    margin: 0;
  }
}
.categories {
  display: flex;
  flex-wrap: wrap;
  text-transform: uppercase;
  gap: var(--Sm, 24px);
}
.category {
  display: flex;
  flex-direction: column;
  p {
    padding-top: 0px;
    margin: var(--2XS, 8px) 0 0;
  }
}
.archive {
  position: absolute;
  top: 237px;
  right: 80px;

  display: flex;
  flex-direction: column;
  align-items: center;

  text-align: right;

  button,
  .archive-header {
    color: var(--50, #939598);
    text-align: right;
    font-family: Indivisible;
    font-size: 16px;
    font-style: normal;
    font-weight: 300;
    line-height: normal;
  }

  .archive-header {
    margin: 0 0 var(--XMd, 40px) 0;
    margin-bottom: 24px;
  }

  button {
    background-color: transparent;
    border: none;

    margin-bottom: 16px;
    cursor: pointer;
  }
}

.error-message {
  display: flex;
  flex-direction: column;
  align-items: center;

  button {
    margin-top: 16px;
    margin-bottom: 64px;
  }
}

@media (min-width: 801px) and (max-width: 1450px) {
  .fashion-fix {
    margin-left: 180px;
    margin-right: 180px;
  }
}

/* Mobile */
@media (max-width: 800px) {
  .desktop-image {
    display: none;
  }
  .image-section {
    margin-left: -24px;
    width: 100vw;
    height: 380px;
    margin-block: 40px;
  }
  .small-text {
    margin-bottom: 40px;
  }
  .categories {
    flex-wrap: nowrap;
    overflow-x: scroll;
    width: 100vw;
    padding-left: 24px;
    margin-left: -24px;
    margin-bottom: 40px;
    gap: 19px;
  }
  .category {
    /* margin-right: 19.2px; */

    img {
      width: 133.566px;
      height: 133.566px;
      object-fit: contain;
    }

    p {
      text-align: center;
      font-family: Indivisible;
      font-size: 9.598px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
    }
  }
}
/* Desktop */

@media (max-width: 800px) {
  .archive {
    position: static;
    top: 0;
    right: 0;
    text-align: center;
    margin-bottom: var(--XMd, 40px);
    button {
      text-align: center;
    }
  }
}
@media (min-width: 801px) {
  .mobile-image {
    display: none;
  }
  .image-section {
    width: 100%;
    height: 440px;
    margin: 32px auto 64px;
  }

  h1 {
    width: 100%;
    text-align: start;
  }

  .small-text {
    display: flex;
    flex-direction: column;
    align-items: center;

    margin-bottom: 64px;
    align-self: stretch;

    p {
      max-width: 921px;
    }
  }

  .categories {
    justify-content: center;

    margin-bottom: 64px;
  }

  .category {
    /* margin-inline: 12px; */

    img {
      width: 167px;
      height: 167px;
      object-fit: contain;
    }

    p {
      text-align: center;
      font-family: Indivisible;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
    }
  }
}

@media (min-width: 1450px) {
  h1 {
    margin-left: 200px;
  }
}
</style>
